<script setup lang="ts">
import { LANDING_CONTENT } from '~/utils/constants'
</script>

<template>
  <section id="features" class="py-24 relative">
    <UContainer>
      <div class="text-center max-w-3xl mx-auto mb-16">
        <UBadge variant="subtle" class="mb-4" label="Features" />
        <h2 class="text-3xl md:text-5xl font-bold mb-6 font-display text-gray-900 dark:text-white">
          Everything you need to succeed
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-400">
          Our platform simulates the real exam environment with advanced AI scoring to give you
          instant feedback.
        </p>
      </div>

      <!-- Bento Grid -->
      <div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-4 gap-6 auto-rows-[300px]">
        <!-- Large Card: Speaking (Spans 2 cols, 2 rows on large) -->
        <div
          class="group relative md:col-span-3 lg:col-span-2 lg:row-span-2 rounded-3xl overflow-hidden bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10"
        >
          <div
            class="absolute inset-0 bg-gradient-to-b from-transparent to-white dark:to-gray-950 z-10 pointer-events-none opacity-50"
          ></div>

          <div class="p-8 h-full flex flex-col relative z-20">
            <div class="mb-auto">
              <div
                class="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4 group-hover:scale-110 transition-transform duration-300"
              >
                <UIcon name="i-heroicons-microphone" class="w-6 h-6" />
              </div>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {{ LANDING_CONTENT.BENTO.SPEAKING.TITLE }}
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                {{ LANDING_CONTENT.BENTO.SPEAKING.DESCRIPTION }}
              </p>
            </div>

            <!-- Mock Visual -->
            <div
              class="mt-8 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-4 transform group-hover:translate-y-2 transition-transform duration-500"
            >
              <div class="flex items-center justify-center gap-1 h-12">
                <div
                  class="w-1 bg-indigo-500 rounded-full animate-pulse h-4"
                  style="animation-duration: 0.5s"
                ></div>
                <div
                  class="w-1 bg-indigo-500 rounded-full animate-pulse h-8"
                  style="animation-duration: 0.8s"
                ></div>
                <div
                  class="w-1 bg-indigo-500 rounded-full animate-pulse h-6"
                  style="animation-duration: 0.6s"
                ></div>
                <div
                  class="w-1 bg-indigo-500 rounded-full animate-pulse h-10"
                  style="animation-duration: 0.4s"
                ></div>
                <div
                  class="w-1 bg-indigo-500 rounded-full animate-pulse h-5"
                  style="animation-duration: 0.7s"
                ></div>
              </div>
              <div
                class="mt-3 text-center text-sm font-medium text-green-500 flex items-center justify-center gap-1"
              >
                <UIcon name="i-heroicons-check-circle" />
                {{ LANDING_CONTENT.BENTO.SPEAKING.FEEDBACK }}
              </div>
            </div>
          </div>
        </div>

        <!-- Medium Card: Writing -->
        <div
          class="group relative md:col-span-3 lg:col-span-2 rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/10 hover:border-orange-500/20"
        >
          <div class="p-8 h-full flex flex-col justify-between">
            <div>
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                  {{ LANDING_CONTENT.BENTO.WRITING.TITLE }}
                </h3>
                <UIcon name="i-heroicons-pencil-square" class="w-6 h-6 text-orange-500" />
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ LANDING_CONTENT.BENTO.WRITING.DESCRIPTION }}
              </p>
            </div>
            <div class="mt-4 space-y-2">
              <div
                class="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 text-xs text-red-600 dark:text-red-400 line-through"
              >
                {{ LANDING_CONTENT.BENTO.WRITING.EXAMPLE_WRONG }}
              </div>
              <div
                class="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/30 text-xs text-green-600 dark:text-green-400 font-medium"
              >
                {{ LANDING_CONTENT.BENTO.WRITING.EXAMPLE_RIGHT }}
              </div>
            </div>
          </div>
        </div>

        <!-- Small Card: Analytics -->
        <div
          class="group relative md:col-span-2 lg:col-span-1 rounded-3xl overflow-hidden bg-gray-900 text-white transition-all duration-300 hover:scale-[1.02]"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700 opacity-90"
          ></div>
          <div
            class="absolute inset-0 opacity-20 mix-blend-overlay"
            :style="{ backgroundImage: `url(${APP_CONFIG.NOISE_SVG_URL})` }"
          ></div>

          <div class="p-6 h-full relative z-10 flex flex-col justify-between">
            <div>
              <h3 class="text-lg font-bold mb-1">{{ LANDING_CONTENT.BENTO.ANALYTICS.TITLE }}</h3>
              <p class="text-indigo-200 text-xs">
                {{ LANDING_CONTENT.BENTO.ANALYTICS.DESCRIPTION }}
              </p>
            </div>
            <div
              class="w-16 h-16 rounded-full border-4 border-white/20 border-t-white mx-auto flex items-center justify-center font-bold text-xl"
            >
              85%
            </div>
          </div>
        </div>

        <!-- Small Card: Mock Tests -->
        <div
          class="group relative md:col-span-2 lg:col-span-1 rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-lg"
        >
          <div class="p-6 h-full flex flex-col justify-center text-center">
            <div
              class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 mx-auto flex items-center justify-center text-blue-600 dark:text-blue-400 mb-3 text-xl font-bold"
            >
              {{ LANDING_CONTENT.BENTO.MOCK_EXAMS.COUNT }}
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white">
              {{ LANDING_CONTENT.BENTO.MOCK_EXAMS.TITLE }}
            </h3>
            <p class="text-xs text-gray-500">{{ LANDING_CONTENT.BENTO.MOCK_EXAMS.DESCRIPTION }}</p>
          </div>
        </div>
      </div>
    </UContainer>
  </section>
</template>
